import { Buttons } from "@neon-purge/shared";
import type { InputManager } from "./input.js";
import { el } from "../ui/dom.js";

/**
 * MOBILE BETA touch controls: a third input source beside keyboard and
 * gamepad. Left zone = virtual movement stick (auto-sprint when pushed to the
 * rim), right zone = drag to look, plus a thumb button cluster. Everything
 * writes into the same InputManager the other sources use — the game and the
 * server never know the difference.
 */

/** Coarse-pointer touch device (phone / tablet). */
export function isTouchDevice(): boolean {
  return (navigator.maxTouchPoints ?? 0) > 0 && window.matchMedia("(pointer: coarse)").matches;
}

const STICK_RADIUS = 56; // px of thumb travel = full deflection

export class TouchControls {
  readonly root: HTMLElement;
  private stickId = -1;
  private lookId = -1;
  private originX = 0;
  private originY = 0;
  private lastLookX = 0;
  private lastLookY = 0;
  private base: HTMLElement;
  private nub: HTMLElement;
  private adsOn = false;
  private adsBtn: HTMLElement;
  private fireBtn: HTMLElement;
  private crouchBtn: HTMLElement | null = null;
  private crouchLatched = false;
  private wasInWater = false;
  // TAP-TO-SHOOT bookkeeping: a quick tap (short + barely moved) anywhere on
  // the free screen fires one shot; a drag is look/move as usual. Touches
  // that start on a button never reach the zones, so buttons are exempt.
  private lookT0 = 0;
  private lookMoved = 0;
  private lookX0 = 0;
  private lookY0 = 0;
  private moveT0 = 0;
  private moveMoved = 0;
  // HOLD-TO-FIRE: keep a still finger down on the look side and the trigger
  // HOLDS (autos spray, the IMPERION scope charges) until you lift — "press
  // the screen, release to shoot". Engages only if the touch hasn't turned
  // into a look drag within the delay; once firing, dragging still aims.
  private lookHoldTimer = 0;
  private lookFiring = false;

  constructor(private input: InputManager, hudRoot: HTMLElement) {
    this.base = el("div", { class: "tc-stick-base" });
    this.nub = el("div", { class: "tc-stick-nub" });
    this.base.append(this.nub);
    this.base.style.display = "none";

    const btn = (cls: string, label: string): HTMLElement =>
      el("div", { class: `tc-btn ${cls}`, text: label });
    const fire = btn("tc-fire", "🔥");
    this.fireBtn = fire; // tap-to-shoot flashes it so the mapping reads
    const jump = btn("tc-jump", "👟");
    // CROUCH / slide on land — and DIVE while swimming (hold to sink underwater)
    const crouch = btn("tc-crouch", "⤓");
    // GRAPPLING HOOK — hold to fire the line and reel in
    const grapple = btn("tc-grapple", "🪝");
    this.adsBtn = btn("tc-ads", "🎯");
    // no reload button — empty mags auto-reload, the slot was dead weight
    const use = btn("tc-use", "✋");
    const nade = btn("tc-nade", "🧨");
    const heal = btn("tc-heal", "✚");
    const knife = btn("tc-knife", "🔪");
    const pause = btn("tc-pause", "⏸");

    this.root = el("div", { class: "tc-root" },
      el("div", { class: "tc-zone tc-zone-move" }),
      el("div", { class: "tc-zone tc-zone-look" }),
      this.base, fire, jump, crouch, grapple, this.adsBtn, use, nade, heal, knife, pause,
    );
    hudRoot.append(this.root);

    // held buttons: press = set bit, release = clear (multi-touch safe since
    // each element tracks its own touches)
    this.holdBtn(fire, Buttons.Fire);
    // CODM-style: the RIGHT fire button is ALSO a look surface — drag while
    // holding to track the target without lifting your shooting thumb
    let fireId = -1, fpx = 0, fpy = 0;
    fire.addEventListener("touchstart", (e) => {
      const t = e.changedTouches[0];
      fireId = t.identifier; fpx = t.clientX; fpy = t.clientY;
    }, { passive: true });
    fire.addEventListener("touchmove", (e) => {
      e.preventDefault();
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier !== fireId) continue;
        this.applyLook(t.clientX - fpx, t.clientY - fpy);
        fpx = t.clientX; fpy = t.clientY;
      }
    }, { passive: false });
    const fireOff = (e: TouchEvent): void => {
      for (const t of Array.from(e.changedTouches)) if (t.identifier === fireId) fireId = -1;
    };
    fire.addEventListener("touchend", fireOff);
    fire.addEventListener("touchcancel", fireOff);
    this.holdBtn(jump, Buttons.Jump);
    this.holdBtn(use, Buttons.Use);
    this.holdBtn(grapple, Buttons.Grapple); // hold to fire + reel the line
    // CROUCH is a TOGGLE (tap on / tap off), not a hold — a thumb parked on it
    // costs a whole finger. On land: tap to crouch, tap to stand. In WATER this
    // reads exactly as the player asked — "click once to dive, click once to
    // float": diving latches Crouch (sink to the seabed), un-latching lets the
    // buoyancy float you back up. Surfacing onto land auto-clears it (setInWater).
    this.crouchBtn = crouch;
    crouch.addEventListener("touchstart", (e) => {
      e.preventDefault();
      this.crouchLatched = !this.crouchLatched;
      crouch.classList.toggle("on", this.crouchLatched);
      if (this.crouchLatched) this.input.touchButtons |= Buttons.Crouch;
      else this.input.touchButtons &= ~Buttons.Crouch;
    }, { passive: false });
    // grenade + knife are edge actions (one throw/stab per tap)
    nade.addEventListener("touchstart", (e) => { e.preventDefault(); nade.classList.add("on"); this.input.touchTap("grenade"); }, { passive: false });
    nade.addEventListener("touchend", () => nade.classList.remove("on"));
    // heal is an edge action too (one shot of the health kit per tap)
    heal.addEventListener("touchstart", (e) => { e.preventDefault(); heal.classList.add("on"); this.input.touchTap("heal"); }, { passive: false });
    heal.addEventListener("touchend", () => heal.classList.remove("on"));
    knife.addEventListener("touchstart", (e) => { e.preventDefault(); knife.classList.add("on"); this.input.touchTap("melee"); }, { passive: false });
    knife.addEventListener("touchend", () => knife.classList.remove("on"));
    // pause: routes through the same path as Esc / the pad's Start button
    pause.addEventListener("touchstart", (e) => { e.preventDefault(); this.input.onEscape?.(); }, { passive: false });
    // aim is a TOGGLE on touch (holding a thumb on it costs a whole finger)
    this.adsBtn.addEventListener("touchstart", (e) => {
      e.preventDefault();
      this.adsOn = !this.adsOn;
      this.adsBtn.classList.toggle("on", this.adsOn);
      if (this.adsOn) this.input.touchButtons |= Buttons.ADS;
      else this.input.touchButtons &= ~Buttons.ADS;
    }, { passive: false });

    const moveZone = this.root.querySelector(".tc-zone-move") as HTMLElement;
    const lookZone = this.root.querySelector(".tc-zone-look") as HTMLElement;
    moveZone.addEventListener("touchstart", this.onMoveStart, { passive: false });
    moveZone.addEventListener("touchmove", this.onMoveMove, { passive: false });
    moveZone.addEventListener("touchend", this.onMoveEnd);
    moveZone.addEventListener("touchcancel", this.onMoveEnd);
    lookZone.addEventListener("touchstart", this.onLookStart, { passive: false });
    lookZone.addEventListener("touchmove", this.onLookMove, { passive: false });
    lookZone.addEventListener("touchend", this.onLookEnd);
    lookZone.addEventListener("touchcancel", this.onLookEnd);
    this.input.touchActive = true;
  }

  /** Called each frame by the match with the local swim state. Surfacing onto
   *  land clears a still-latched dive so you don't walk out stuck in a crouch;
   *  also toggles the ⤓/⤒ glyph so the button reads "dive" vs "rise". */
  setInWater(inWater: boolean): void {
    if (this.crouchBtn) this.crouchBtn.textContent = inWater ? (this.crouchLatched ? "⤒" : "⤓") : "⤓";
    if (this.wasInWater && !inWater && this.crouchLatched) {
      this.crouchLatched = false;
      this.crouchBtn?.classList.remove("on");
      this.input.touchButtons &= ~Buttons.Crouch;
    }
    this.wasInWater = inWater;
  }

  private holdBtn(elm: HTMLElement, bit: number): void {
    elm.addEventListener("touchstart", (e) => {
      e.preventDefault();
      elm.classList.add("on");
      this.input.touchButtons |= bit;
      if (bit === Buttons.Fire) this.input.touchTap("fire"); // semi-autos need the edge latch
    }, { passive: false });
    const off = (): void => { elm.classList.remove("on"); this.input.touchButtons &= ~bit; };
    elm.addEventListener("touchend", off);
    elm.addEventListener("touchcancel", off);
  }

  /** TAP-TO-SHOOT: quick tap on free screen = one shot (the 🔥 button stays
   *  for held fire — and so newcomers can see how shooting works). */
  private tapFire(): void {
    this.input.touchTap("fire");
    this.fireBtn.classList.add("on");
    window.setTimeout(() => this.fireBtn.classList.remove("on"), 110);
  }

  // ---- virtual stick ----
  private onMoveStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (this.stickId !== -1) return;
    const t = e.changedTouches[0];
    this.stickId = t.identifier;
    this.originX = t.clientX;
    this.originY = t.clientY;
    this.moveT0 = performance.now();
    this.moveMoved = 0;
    this.base.style.display = "block";
    this.base.style.left = `${t.clientX}px`;
    this.base.style.top = `${t.clientY}px`;
    this.nub.style.transform = "translate(-50%, -50%)";
  };

  private onMoveMove = (e: TouchEvent): void => {
    e.preventDefault();
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.stickId) continue;
      let dx = t.clientX - this.originX;
      let dy = t.clientY - this.originY;
      this.moveMoved = Math.max(this.moveMoved, Math.hypot(dx, dy));
      const len = Math.hypot(dx, dy);
      if (len > STICK_RADIUS) { dx *= STICK_RADIUS / len; dy *= STICK_RADIUS / len; }
      this.nub.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      const mx = dx / STICK_RADIUS;
      const mz = -dy / STICK_RADIUS; // screen-up = forward
      this.input.touchMoveX = Math.abs(mx) < 0.12 ? 0 : mx;
      this.input.touchMoveZ = Math.abs(mz) < 0.12 ? 0 : mz;
      // rim push = auto-sprint (no spare finger for a sprint key on glass)
      if (Math.hypot(mx, mz) > 0.92 && mz > 0.3) this.input.touchButtons |= Buttons.Sprint;
      else this.input.touchButtons &= ~Buttons.Sprint;
    }
  };

  private onMoveEnd = (e: TouchEvent): void => {
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.stickId) continue;
      this.stickId = -1;
      this.input.touchMoveX = 0;
      this.input.touchMoveZ = 0;
      this.input.touchButtons &= ~Buttons.Sprint;
      this.base.style.display = "none";
      if (performance.now() - this.moveT0 < 220 && this.moveMoved < 12) this.tapFire();
    }
  };

  // ---- look drag ----
  private onLookStart = (e: TouchEvent): void => {
    e.preventDefault();
    if (this.lookId !== -1) return;
    const t = e.changedTouches[0];
    this.lookId = t.identifier;
    this.lastLookX = t.clientX;
    this.lastLookY = t.clientY;
    this.lookT0 = performance.now();
    this.lookX0 = t.clientX;
    this.lookY0 = t.clientY;
    this.lookMoved = 0;
    window.clearTimeout(this.lookHoldTimer);
    this.lookHoldTimer = window.setTimeout(() => {
      if (this.lookId === -1 || this.lookMoved >= 12) return; // became a drag
      this.lookFiring = true;
      this.input.touchButtons |= Buttons.Fire;
      this.input.touchTap("fire"); // semi-autos need the press edge
      this.fireBtn.classList.add("on");
    }, 260);
  };

  private applyLook(dx: number, dy: number): void {
    // FLICK ACCELERATION (standard in mobile shooters): slow drags stay
    // precise, fast swipes turn much further — a 180 shouldn't need three
    // thumb strokes. Speed-scaled multiplier 1x → ~2.9x, kicking in early.
    const speed = Math.hypot(dx, dy);
    const accel = 1 + Math.min(1.9, Math.max(0, (speed - 3) / 12));
    // "Touch look sensitivity ×" in Settings scales this (own dial, 0.4–3)
    const s = 0.011 * (this.input.sens.touchMul ?? 1) * accel * this.input.zoomMult * (this.adsOn ? 0.6 : 1);
    this.input.yaw -= dx * s;
    const limit = Math.PI / 2 - 0.02;
    this.input.pitch = Math.max(-limit, Math.min(limit, this.input.pitch - dy * s));
    this.input.lastLookAt = performance.now();
  }

  private onLookMove = (e: TouchEvent): void => {
    e.preventDefault();
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.lookId) continue;
      this.lookMoved = Math.max(this.lookMoved, Math.hypot(t.clientX - this.lookX0, t.clientY - this.lookY0));
      this.applyLook(t.clientX - this.lastLookX, t.clientY - this.lastLookY);
      this.lastLookX = t.clientX;
      this.lastLookY = t.clientY;
    }
  };

  private onLookEnd = (e: TouchEvent): void => {
    for (const t of Array.from(e.changedTouches)) {
      if (t.identifier !== this.lookId) continue;
      this.lookId = -1;
      window.clearTimeout(this.lookHoldTimer);
      if (this.lookFiring) {
        // finger lifts → trigger releases (this is what LOOSES the charged
        // IMPERION beam: hold to charge in the scope, un-tap to shoot)
        this.lookFiring = false;
        this.input.touchButtons &= ~Buttons.Fire;
        this.fireBtn.classList.remove("on");
      } else if (performance.now() - this.lookT0 < 220 && this.lookMoved < 12) {
        this.tapFire(); // quick tap that never became a look drag → one shot
      }
    }
  };

  /** Show/hide the on-screen control layer ("On-screen buttons" setting, default
   *  on — off is for controller play). The `tc-hidden` class hides every button,
   *  the joystick and both drag zones (so nothing but the pad drives input) — but
   *  DELIBERATELY keeps the ⏸ PAUSE button (`.tc-pause`) visible: you still need
   *  to pause on a controller. Clears any latched button/move so a held control
   *  can't stick when it vanishes mid-press. */
  setVisible(visible: boolean): void {
    this.root.classList.toggle("tc-hidden", !visible);
    this.input.touchActive = visible;
    if (visible) return;
    window.clearTimeout(this.lookHoldTimer);
    this.input.touchMoveX = 0;
    this.input.touchMoveZ = 0;
    this.input.touchButtons = 0;
    this.adsOn = false;
    this.adsBtn.classList.remove("on");
    this.crouchLatched = false;
    this.crouchBtn?.classList.remove("on");
    this.lookFiring = false;
    this.stickId = this.lookId = -1;
  }

  dispose(): void {
    window.clearTimeout(this.lookHoldTimer);
    this.input.touchMoveX = 0;
    this.input.touchMoveZ = 0;
    this.input.touchButtons = 0;
    this.input.touchActive = false;
    this.root.remove();
  }
}
